import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Icon from "@material-ui/core/Icon";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardHeader from "components/Card/CardHeader.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";

import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import api from "api";

const styles = {
  container: {
    padding: "30px",
    background: "#f8fafc",
    minHeight: "100vh"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px"
  },
  titleArea: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.02em"
  },
  pageSubtitle: {
    fontSize: "15px",
    color: "#64748b",
    margin: 0
  },
  headerActions: {
    display: "flex",
    gap: "12px"
  },
  
  // KPI Cards
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "32px"
  },
  kpiCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.05)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    display: "flex",
    gap: "16px",
    alignItems: "flex-start"
  },
  kpiIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px"
  },
  kpiContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  kpiValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1
  },
  kpiLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#64748b"
  },
  kpiTrend: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "4px"
  },

  // Filters
  filterToolbar: {
    background: "#fff",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid rgba(0,0,0,0.05)",
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  filterMainRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "nowrap"
  },
  searchBox: {
    position: "relative",
    flex: 1,
    minWidth: "200px"
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    fontSize: "20px"
  },
  searchInput: {
    width: "100%",
    padding: "10px 12px 10px 40px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    background: "#fff",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
    }
  },
  filterControl: {
    minWidth: "250px"
  },
  filterInputLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    marginBottom: "4px",
    position: "relative",
    transform: "none"
  },
  filterSelect: {
    "& .MuiSelect-select": {
      padding: "10px 12px",
      fontSize: "14px"
    },
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    "&::before, &::after": { display: "none" }
  },
  tagRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  filterTag: {
    padding: "4px 12px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  clearAll: {
    fontSize: "12px",
    color: "#64748b",
    cursor: "pointer",
    marginLeft: "8px",
    "&:hover": { color: "#0f172a" }
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  actionBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    fontSize: "14px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  yearSection: { marginBottom: "24px" },
  yearTitle: { fontWeight: "bold", fontSize: "15px", color: "#1e293b", borderBottom: "2px solid #f1f5f9", paddingBottom: "6px", marginBottom: "12px" },
  semTitle: { fontSize: "13px", fontWeight: "600", color: "#4f46e5", marginBottom: "8px", marginTop: "12px" },
  courseCard: {
    border: "1px solid #f1f5f9",
    borderRadius: "12px",
    padding: "16px",
    background: "#fff",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    marginBottom: "8px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    transition: "all 0.2s",
    "&:hover": { borderColor: "#e2e8f0", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)" }
  },
  courseCode: { fontWeight: "bold", color: "#4f46e5", fontSize: "12px", marginBottom: "2px" },
  courseName: { fontSize: "14px", fontWeight: 600, color: "#0f172a" },
  courseMeta: { fontSize: "12px", color: "#64748b", marginTop: "4px" },
  creditBadge: { fontSize: "11px", background: "#f5f3ff", color: "#4f46e5", borderRadius: "6px", padding: "2px 8px", fontWeight: "600" },
  emptyState: { textAlign: "center", color: "#999", padding: "40px" },
  noProgramme: { textAlign: "center", color: "#bbb", padding: "60px 20px" }
};

// Group courses by year and semester
function groupCourses(courses) {
  const map = {};
  courses.forEach(c => {
    const year = c.yearOfStudy || c.year || 1;
    // Handle semester which could be an object or a number/string
    let sem = c.semester || c.semesterNumber || 1;
    if (sem && typeof sem === "object") {
      sem = sem.name || sem.number || sem.id || 1;
    }
    
    if (!map[year]) map[year] = {};
    if (!map[year][sem]) map[year][sem] = [];
    map[year][sem].push(c);
  });
  return map;
}

class CurriculumMap extends Component {
  state = {
    programmes: [],
    courses: [],
    selectedProgramme: "",
    loadingProgrammes: true,
    loadingCourses: false,
    error: null
  };

  componentDidMount() {
    this.fetchProgrammes();
  }

  fetchProgrammes = async () => {
    this.setState({ loadingProgrammes: true });
    try {
      const res = await api.get("/programmes");
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      this.setState({ programmes: list, loadingProgrammes: false });
    } catch (e) {
      this.setState({ loadingProgrammes: false, error: "Could not load programmes." });
    }
  };

  fetchCourses = async programmeId => {
    if (!programmeId) { this.setState({ courses: [] }); return; }
    this.setState({ loadingCourses: true, error: null });
    try {
      const res = await api.get("/courses", { params: { programmeId } });
      const d = res.data.data || res.data;
      this.setState({ courses: Array.isArray(d) ? d : d.data || [], loadingCourses: false });
    } catch (e) {
      this.setState({ courses: [], loadingCourses: false, error: "Could not load courses." });
    }
  };

  handleProgrammeChange = e => {
    const v = e.target.value;
    this.setState({ selectedProgramme: v }, () => this.fetchCourses(v));
  };

  onFilterClick = (show) => {
    // Handled internally by DataTable to toggle toolbar filters
  };

  onSortClick = (event) => {
    // Can be used to show sort options
  };

  onViewClick = (density) => {
    // Density handled internally by DataTable
  };

  render() {
    const { classes, history } = this.props;
    const { programmes, courses, selectedProgramme, loadingProgrammes, loadingCourses, error } = this.state;

    const grouped = groupCourses(courses);
    const years = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

    const currentProg = programmes.find(p => String(p.id) === String(selectedProgramme));

    return (
      <div className={classes.container}>
        <div className={classes.headerRow}>
          <div className={classes.titleArea}>
            <h1 className={classes.pageTitle}>Curriculum Map</h1>
            <p className={classes.pageSubtitle}>Explore courses organised by programme, year of study, and semester.</p>
          </div>
          <div className={classes.headerActions}>
            <Button color="primary" onClick={() => history.push("/bwillis/academic/courses")} style={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, padding: "10px 20px" }}>
              <Icon style={{ marginRight: "8px" }}>library_books</Icon> Manage Courses
            </Button>
          </div>
        </div>

        <div className={classes.kpiRow}>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f5f3ff", color: "#4f46e5" }}>
              <Icon>school</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{programmes.length}</span>
              <span className={classes.kpiLabel}>Total Programmes</span>
              <span className={classes.kpiTrend}>Total catalog</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#eff6ff", color: "#2563eb" }}>
              <Icon>menu_book</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{courses.length}</span>
              <span className={classes.kpiLabel}>Courses in View</span>
              <span className={classes.kpiTrend}>This programme</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f0fdf4", color: "#16a34a" }}>
              <Icon>event</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{years.length}</span>
              <span className={classes.kpiLabel}>Academic Years</span>
              <span className={classes.kpiTrend}>In structure</span>
            </div>
          </div>
        </div>

        <div className={classes.filterToolbar}>
          <div className={classes.filterMainRow}>
            <div className={classes.searchBox}>
              <Icon className={classes.searchIcon}>search</Icon>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search curriculum..."
                value={this.state.searchValue || ""}
                onChange={e => this.setState({ searchValue: e.target.value })}
              />
            </div>

            {loadingProgrammes ? (
              <CircularProgress size={20} />
            ) : (
              <FormControl className={classes.filterControl}>
                <InputLabel className={classes.filterInputLabel}>Select Programme</InputLabel>
                <Select
                  value={selectedProgramme}
                  onChange={this.handleProgrammeChange}
                  className={classes.filterSelect}
                  disableUnderline
                >
                  <MenuItem value=""><em>— Select Programme —</em></MenuItem>
                  {programmes.map(p => (
                    <MenuItem key={p.id} value={String(p.id)}>{p.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <button className={classes.actionBtn}>
              <Icon style={{ fontSize: "18px" }}>swap_vert</Icon> Sort
            </button>
            <button className={classes.actionBtn}>
              <Icon style={{ fontSize: "18px" }}>file_upload</Icon> Export <Icon>expand_more</Icon>
            </button>
          </div>

          {currentProg && (
            <div className={classes.tagRow}>
              <div className={classes.filterTag}>
                {currentProg.name}
                <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ selectedProgramme: "" }, () => this.fetchCourses(""))}>close</Icon>
              </div>
              <span className={classes.clearAll} onClick={() => this.setState({ selectedProgramme: "" }, () => this.fetchCourses(""))}>Clear all</span>
            </div>
          )}
        </div>

        {error && <p className={classes.errorMsg}>{error}</p>}

        <DataTable
          title="Programme Curriculum"
          subtitle={selectedProgramme ? `Curriculum structure` : "Please select a programme"}
          searchValue={this.state.searchValue}
        >
          {!selectedProgramme ? (
            <div className={classes.noProgramme}>
              <Icon style={{ fontSize: "48px", color: "#e2e8f0", display: "block", margin: "0 auto 12px" }}>school</Icon>
              <p style={{ color: "#64748b" }}>Select a programme above to view its curriculum.</p>
            </div>
          ) : loadingCourses ? (
            <div style={{ textAlign: "center", padding: "40px" }}><CircularProgress /></div>
          ) : courses.length === 0 ? (
            <p className={classes.emptyState}>No courses found for this programme.</p>
          ) : (
            <div style={{ padding: "24px" }}>
              {years.map(year => (
                <div key={year} className={classes.yearSection}>
                  <div className={classes.yearTitle}>Year {year}</div>
                  {Object.keys(grouped[year]).sort((a, b) => {
                    const na = parseInt(String(a).replace(/^\D+/g, ""), 10);
                    const nb = parseInt(String(b).replace(/^\D+/g, ""), 10);
                    if (!isNaN(na) && !isNaN(nb)) return na - nb;
                    return String(a).localeCompare(String(b));
                  }).map(sem => (
                    <div key={sem}>
                      <div className={classes.semTitle}>
                        {String(sem).toLowerCase().includes("semester") ? sem : `Semester ${sem}`}
                      </div>
                      <GridContainer>
                        {grouped[year][sem].map(course => (
                          <GridItem key={course.id} xs={12} sm={6} md={4}>
                            <div className={classes.courseCard}>
                              <div>
                                {course.code && <div className={classes.courseCode}>{course.code}</div>}
                                <div className={classes.courseName}>{course.name}</div>
                                <div className={classes.courseMeta}>
                                  {course.type ? `${course.type} · ` : ""}
                                  {course.creditUnits != null ? `${course.creditUnits} CUs` : ""}
                                </div>
                              </div>
                              {course.creditUnits != null && (
                                <div className={classes.creditBadge}>{course.creditUnits} CU</div>
                              )}
                            </div>
                          </GridItem>
                        ))}
                      </GridContainer>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </DataTable>
      </div>
    );
  }
}

CurriculumMap.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(CurriculumMap));
